import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { classSpan, formatRange, instructorLoad } from "./plannerUtils";
import { colorFor, initials } from "./plannerConstants";

function Row({ sc, note, onSelect }) {
  return (
    <button type="button" className="sp-rr" style={{ "--c": colorFor(sc.category) }} onClick={() => onSelect(sc.id)}>
      <i />
      <span>
        <b>{sc.title}</b>
        <small>{note}</small>
      </span>
      <ChevronRightIcon sx={{ fontSize: 16 }} />
    </button>
  );
}

const spanText = (sc) => {
  const s = classSpan(sc);
  return s ? formatRange(s.start, s.end) : "Not scheduled";
};

export default function ReadinessPanel({ report, classes, instructorNames, onSelect, onClose }) {
  const { blocking, warnings, unassigned, issueCount, ready } = report;
  const byId = (id) => classes.find((c) => c.id === id);
  const load = instructorLoad(classes);
  const loadRows = Object.entries(load).sort((a, b) => b[1] - a[1]);
  const max = Math.max(...loadRows.map(([, n]) => n), 1);

  const conflictRows = (list) =>
    list.map((c, i) => {
      const a = byId(c.a);
      const b = byId(c.b);
      return a && b ? (
        <Row key={`${c.kind}-${i}`} sc={b} onSelect={onSelect} note={`${c.kind === "instructor" ? instructorNames[a.instructorId] || "Instructor" : c.what} also on ${a.title} · ${spanText(b)}`} />
      ) : null;
    });

  return (
    <div className="sp-rd" data-testid="planner-readiness">
      <div className="sp-rd-h">
        <div>
          <span className="sp-eb">Before publishing</span>
          <h3>{ready ? "Ready to publish" : `${issueCount} ${issueCount === 1 ? "item" : "items"} to review`}</h3>
        </div>
        <button type="button" className="sp-pib" onClick={onClose} aria-label="Close">
          <CloseIcon sx={{ fontSize: 16 }} />
        </button>
      </div>
      {ready && (
        <div className="sp-al ok">
          <CheckIcon sx={{ fontSize: 15 }} />
          <div><b>No conflicts</b><span>Every class has an instructor.</span></div>
        </div>
      )}
      {blocking.length > 0 && (
        <div className="sp-rs">
          <div className="sp-rs-t"><WarningAmberIcon sx={{ fontSize: 13 }} />Instructor conflicts <em className="bad">{blocking.length}</em></div>
          {conflictRows(blocking)}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="sp-rs">
          <div className="sp-rs-t"><WarningAmberIcon sx={{ fontSize: 13 }} />Possible room conflicts <em className="warn">{warnings.length}</em></div>
          {conflictRows(warnings)}
        </div>
      )}
      {unassigned.length > 0 && (
        <div className="sp-rs">
          <div className="sp-rs-t">Needs instructor <em className="warn">{unassigned.length}</em></div>
          {unassigned.map((sc) => (
            <Row key={sc.id} sc={sc} onSelect={onSelect} note={`${spanText(sc)}${sc.location ? ` · ${sc.location}` : ""}`} />
          ))}
        </div>
      )}
      {loadRows.length > 0 && (
        <div className="sp-rs">
          <div className="sp-rs-t">Instructor load <span className="sp-sm">session days</span></div>
          {loadRows.map(([id, n]) => (
            <div key={id} className="sp-ld">
              <span className="sp-pav">{initials(instructorNames[id] || "?")}</span>
              <span>{instructorNames[id] || `User ${id}`}</span>
              <div className="sp-ld-b"><i style={{ width: `${(n / max) * 100}%` }} /></div>
              <b className="sp-mo">{n}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
